import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import {
    Box,
    Dialog,
    DialogContent,
    DialogTitle,
    DialogActions,
    Button,
    Grid,
    IconButton,
    Tooltip,
    CircularProgress,
    Snackbar,
    Alert
} from "@mui/material";
import { Excalidraw, MainMenu } from "@excalidraw/excalidraw";
import "@excalidraw/excalidraw/index.css";
import Sidebar from "./Sidebar";
import AlertDialog from "../../components/alert/alertComponent";
import CreateWhiteboard from "./CreateWhiteboard";
import EditIcon from '@mui/icons-material/Edit';
import { whiteboardService } from '../../services/api/whiteboardService';

export default function WhiteBoard({
    handleClose,
    open,
    boardPermissions,
    boardId
}) {
    const [excalidrawAPI, setExcalidrawAPI] = useState(null);
    const [boards, setBoards] = useState([]);
    const [selectedWhiteBoard, setSelectedWhiteBoard] = useState(null);
    const [alertOpen, setAlertOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isButtonLoading, setIsButtonLoading] = useState(false);
    const [isDeleteButtonLoading, setIsDeleteButtonLoading] = useState(false);
    const [createFormOpen, setCreateFormOpen] = useState(false);
    const [editingBoard, setEditingBoard] = useState(null);
    const [deletingBoard, setDeletingBoard] = useState(null);
    const [IsshowErrorToast, setIsShowErrorToast] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");

    const handleErrorClose = () => {
        setIsShowErrorToast(false);
        setErrorMessage("");
    };

    useEffect(() => {
        if (open && boardId) {
            loadWhiteboards();
        }
    }, [open, boardId]);

    useEffect(() => {
        if (boards.length > 0 && !selectedWhiteBoard) {
            setSelectedWhiteBoard(boards[0]);
        }
    }, [boards, selectedWhiteBoard]);

    useEffect(() => {
        if (excalidrawAPI && selectedWhiteBoard?.scenes && typeof selectedWhiteBoard.scenes === 'object') {
            excalidrawAPI.updateScene({
                elements: selectedWhiteBoard.scenes.elements || [],
                appState: selectedWhiteBoard.scenes.appState || {}
            });
        } else if (excalidrawAPI && selectedWhiteBoard && !selectedWhiteBoard.scenes) {
            excalidrawAPI.updateScene({ elements: [] });
        }
    }, [excalidrawAPI, selectedWhiteBoard]);

    useEffect(() => {
        if (!open) {
            setBoards([]);
            setSelectedWhiteBoard(null);
            setExcalidrawAPI(null);
            setEditingBoard(null);
            setDeletingBoard(null);
            setIsShowErrorToast(false);
            setErrorMessage("");
        }
    }, [open]);

    const loadWhiteboards = async () => {
        try {
            setIsLoading(true);
            const data = await whiteboardService.getWhiteboards(boardId);
            setBoards(data);
        } catch (error) {
            setIsShowErrorToast(true);
            setErrorMessage(error?.response?.data?.message || "Failed to load whiteboards");
        } finally {
            setIsLoading(false);
        }
    };

    const handleDelete = (whiteboard) => {
        setDeletingBoard(whiteboard);
        setAlertOpen(true);
    };
    const handleDeleteClose = () => {
        setAlertOpen(false);
        setDeletingBoard(null);
    };
    const handleDeleteConfirm = async () => {
        try {
            setIsDeleteButtonLoading(true);
            await whiteboardService.deleteWhiteboard(deletingBoard?.id, boardId);
            if (deletingBoard?.id === selectedWhiteBoard?.id) {
                setSelectedWhiteBoard(boards?.length > 1 ? boards[1] : null);
            }
            setBoards((prev) => prev.filter((board) => board.id !== deletingBoard.id));
            setAlertOpen(false);
            handleDeleteClose();
        } catch (error) {
            setIsShowErrorToast(true);
            setErrorMessage(error?.response?.data?.message || "Failed to delete whiteboard");
        } finally {
            setIsDeleteButtonLoading(false);
        }
    };

    const handleCreateOpen = (board = null) => {
        setEditingBoard(board);
        setCreateFormOpen(true);
    }
    const handleCreateClose = () => {
        setCreateFormOpen(false);
        setEditingBoard(null);
    }
    const handleCreate = async (name) => {
        try {
            setIsButtonLoading(true);
            if (editingBoard) {
                const updatingWhiteBoard = {
                    board_id: boardId,
                    name,
                    scenes: editingBoard?.scenes
                }
                // Update existing board
                await whiteboardService.updateWhiteboard(editingBoard?.id, updatingWhiteBoard);
                setBoards(prev => prev.map(board =>
                    board.id === editingBoard.id
                        ? { ...board, name }
                        : board
                ));
                setSelectedWhiteBoard(prev => ({ ...prev, name }));
            } else {
                // Create new board
                const whiteboardData = {
                    board_id: boardId,
                    name
                }
                const response = await whiteboardService.createWhiteboard(whiteboardData);
                setBoards((prev) => [response?.whiteboardData, ...prev]);
                setSelectedWhiteBoard(response?.whiteboardData);

                // Clear the canvas for new board
                if (excalidrawAPI) {
                    excalidrawAPI.updateScene({ elements: [] });
                }
            }
            setCreateFormOpen(false);
        } catch (error) {
            setIsShowErrorToast(true);
            setErrorMessage(error?.response?.data?.message || "Failed to save whiteboard");
        } finally {
            setIsButtonLoading(false);
        }
    };

    const handleSelect = (whiteBoard) => {
        setSelectedWhiteBoard(whiteBoard);
    };

    const handleSave = async () => {
        if (excalidrawAPI && selectedWhiteBoard) {
            try {
                setIsButtonLoading(true);
                const elements = excalidrawAPI.getSceneElements();
                const appState = excalidrawAPI.getAppState();
                const files = excalidrawAPI.getFiles();

                const scenes = {
                    elements,
                    appState: {
                        viewBackgroundColor: appState.viewBackgroundColor,
                        zoom: appState.zoom,
                        scrollX: appState.scrollX,
                        scrollY: appState.scrollY
                    },
                    files
                };

                const whiteboardData = {
                    board_id: boardId,
                    name: selectedWhiteBoard.name,
                    scenes: scenes
                }

                const response = await whiteboardService.updateWhiteboard(selectedWhiteBoard.id, whiteboardData);

                // Update local state
                setBoards(prev => prev.map(board =>
                    board.id === selectedWhiteBoard.id
                        ? { ...board, scenes: response?.scenes }
                        : board
                ));
                setSelectedWhiteBoard(prev => ({ ...prev, scenes: response?.scenes }));

                handleClose();
            } catch (error) {
                setIsShowErrorToast(true);
                setErrorMessage(error?.response?.data?.message || "Failed to save whiteboard");
            } finally {
                setIsButtonLoading(false);
            }
        }
    };

    return (
        <>
            <Dialog
                onClose={handleClose}
                open={open}
                fullWidth
                maxWidth={'xl'}
            >
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box>{(selectedWhiteBoard?.name && !isLoading) ? selectedWhiteBoard.name : 'White Board'}</Box>
                    {(selectedWhiteBoard?.name && !isLoading && boardPermissions?.includes("card_edit")) && (
                        <Tooltip title={'Edit Name'} placement="right">
                            <IconButton
                                size="small"
                                onClick={() => handleCreateOpen(selectedWhiteBoard)}
                                disabled={isButtonLoading}
                            >
                                <EditIcon />
                            </IconButton>
                        </Tooltip>
                    )}
                </DialogTitle>

                <DialogContent
                    sx={{
                        height: '80vh',
                        padding: 0
                    }}
                >
                    {
                        isLoading ?
                            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                                <CircularProgress />
                            </Box> :

                            <Grid container sx={{ height: '100%' }}>
                                <Grid item xs={2}>
                                    <Sidebar
                                        boards={boards}
                                        selectedWhiteBoard={selectedWhiteBoard}
                                        onCreateOpen={handleCreateOpen}
                                        onSelect={handleSelect}
                                        onDelete={handleDelete}
                                        boardPermissions={boardPermissions}
                                        isLoading={isButtonLoading}
                                    />
                                </Grid>
                                <Grid item xs={10} pr={1} sx={{ height: '100%' }}>
                                    <Box
                                        sx={{
                                            width: '100%',
                                            height: '100%',
                                        }}
                                    >
                                        {boards?.length < 1 ?
                                            <Box
                                                height={'100%'}
                                                display={'flex'}
                                                alignItems={'center'}
                                                justifyContent={'center'}
                                            >
                                                No Whiteboards Found
                                            </Box> :
                                            <Excalidraw
                                                // ref={excalidrawRef}
                                                excalidrawAPI={(api) => setExcalidrawAPI(api)}
                                                viewModeEnabled={!boardPermissions?.includes("card_edit")}
                                                initialData={selectedWhiteBoard?.scenes && typeof selectedWhiteBoard.scenes === 'object'
                                                    ? selectedWhiteBoard.scenes
                                                    : { elements: [], appState: {} }}
                                                UIOptions={{
                                                    tools: {
                                                        image: false
                                                    }
                                                }}
                                            >
                                                <MainMenu>
                                                    <MainMenu.DefaultItems.SaveAsImage />
                                                    <MainMenu.DefaultItems.ClearCanvas />
                                                    <MainMenu.DefaultItems.SearchMenu />
                                                    <MainMenu.DefaultItems.ChangeCanvasBackground />
                                                </MainMenu>
                                            </Excalidraw>
                                        }
                                    </Box>
                                </Grid>
                            </Grid>
                    }
                </DialogContent>
                <DialogActions sx={{ borderTop: '1px solid #efefef' }}>
                    <Button
                        onClick={handleClose}
                        disabled={isButtonLoading}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleSave}
                        variant="contained"
                        disabled={!boardPermissions?.includes("card_edit") || isLoading || isButtonLoading}
                    >
                        {
                            !createFormOpen && isButtonLoading ?
                                <CircularProgress size={20} color='inherit' /> :
                                'Save'
                        }
                    </Button>
                </DialogActions>
                {
                    alertOpen &&
                    <AlertDialog
                        handleClose={handleDeleteClose}
                        alertopen={alertOpen}
                        message={
                            <>
                                Are you sure want to delete <b>{deletingBoard?.name}</b> white board?
                            </>
                        }
                        title="Alert"
                        confirmbutton
                        confirmFunc={() => handleDeleteConfirm()}
                        loader={isDeleteButtonLoading}
                    />
                }
                {createFormOpen && (
                    <CreateWhiteboard
                        open={createFormOpen}
                        onClose={handleCreateClose}
                        onCreate={handleCreate}
                        editingBoard={editingBoard}
                        isLoading={isButtonLoading}
                    />)}
            </Dialog>
            <Snackbar
                open={IsshowErrorToast}
                autoHideDuration={6000}
                onClose={handleErrorClose}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                sx={{ zIndex: 9999 }}
            >
                <Alert onClose={handleErrorClose} severity="error" variant="filled" sx={{ width: '100%' }}>
                    {errorMessage}
                </Alert>
            </Snackbar>
        </>
    );
}

WhiteBoard.propTypes = {
    handleClose: PropTypes.func.isRequired,
    open: PropTypes.bool.isRequired,
    boardPermissions: PropTypes.arrayOf(PropTypes.string).isRequired,
    boardId: PropTypes.number.isRequired
};